/**
 * Session Management API Service
 * Handles all session-related API calls to the backend
 */

import { api } from "@/redux/_axios";
import {
  SessionListResponse,
  AccountSharingResponse,
  LogoutSessionResponse,
  DeviceInfo,
} from "@/types/session";

const SESSION_API_BASE = "/sessions";
const ACCOUNT_SHARING_BASE = "/check-sharing";
const LOGOUT_DEVICE_BASE = "/logout-device";
const LOGOUT_ALL_OTHERS_BASE = "/logout-all-other-devices";

/**
 * SessionAPI - API service for session management
 */
export const SessionAPI = {
  /**
   * Get list of active sessions for the current user
   */
  getActiveSessions: (): Promise<SessionListResponse> =>
    api.get(SESSION_API_BASE).then((res) => res.data),

  /**
   * Check for account sharing and anomalies
   */
  checkAccountSharing: (): Promise<AccountSharingResponse> =>
    api.get(ACCOUNT_SHARING_BASE).then((res) => res.data),

  /**
   * Logout from a specific device/session
   */
  logoutFromDevice: (
    sessionId: string | number,
  ): Promise<LogoutSessionResponse> =>
    api.post(`${LOGOUT_DEVICE_BASE}/${sessionId}`).then((res) => res.data),

  /**
   * Logout from all other devices (keep current session active)
   */
  logoutAllOtherDevices: (): Promise<LogoutSessionResponse> =>
    api.post(LOGOUT_ALL_OTHERS_BASE).then((res) => res.data),

  /**
   * Update session activity (keep session alive)
   */
  updateSessionActivity: (sessionId?: string): Promise<any> => {
    const payload: any = {};

    // If sessionId provided, include it
    if (sessionId) {
      payload.session_id = sessionId;
    } else {
      // Try to get from localStorage (stored during login)
      const storedSessionId = localStorage.getItem("current_session_id");
      if (storedSessionId) {
        payload.session_id = storedSessionId;
      }
    }

    return api.post(`${SESSION_API_BASE}/activity`, payload).catch(() => null);
  },

  /**
   * Get session details
   */
  getSessionDetails: (sessionId: string | number): Promise<any> =>
    api.get(`${SESSION_API_BASE}/${sessionId}`).then((res) => res.data),

  /**
   * Submit device fingerprint with login
   */
  submitDeviceFingerprint: (fingerprint: DeviceInfo): Promise<any> =>
    api.post(`${SESSION_API_BASE}/fingerprint`, fingerprint).catch(() => null),
};

export default SessionAPI;
